import { useMemo } from 'react';
import axiosInstance, { endpoints, fetcher } from 'src/utils/axios';
import useSWR from 'swr';
import { client, joinErrors } from './client';

export function useGetSelectFoodList(
  start_date: string | null,
  end_date: string | null,
  title: string | null
) {
  let URL = endpoints.foods.foodsList;

  const params = new URLSearchParams();

  if (start_date && end_date) {
    params.append('start_date', `${start_date}`);
    params.append('end_date', `${end_date}`);
  }

  if (title) {
    params.append('title', title);
  }

  if (Array.from(params).length > 0) {
    URL = `${URL}?${params}`;
  }

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    const DATES = isLoading ? [] : data ? data?.data?.map((item: any) => item.date) : [];

    return {
      foods: (data as any) || { data: [] },
      tabs: DATES,
      foodsLoading: isLoading,
      foodsError: error,
      foodsValidating: isValidating,
      foodsEmpty: !isLoading && !data,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}
export function useGetFoodDetails(id: any, date: string | null) {
  const URL = `${endpoints.foods.foodDetails(id)}?date=${date}`;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      food: (data as any) || [],
      foodLoading: isLoading,
      foodError: error,
      foodValidating: isValidating,
      foodEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function GetFoodCustomersStickers(
  date: string | null,
  deposition: 'inline' | 'attachment' | null,
  phone_number: string | null,
  size: 'a4' | '100x150' | '100x100' | '100x175' | '100x75'
) {
  let URL = `${endpoints.foods.stickers}${size}/`;
  const params = new URLSearchParams();
  if (date) {
    params.append('date', `${date}`);
  }
  if (phone_number) {
    params.append('phone_number', phone_number);
  }

  if (deposition) {
    params.append('deposition', deposition);
  }

  if (Array.from(params).length > 0) {
    URL = `${URL}?${params}`;
  }
  try {
    const response = await axiosInstance.get(URL, { responseType: 'blob' });

    return response.data;
  } catch (err) {
    throw new Error(err);
  }
}

export async function GetSmallFoodStickers(width: number, height: number, date: string | null) {
  let URL = `${endpoints.foods.food_stickers}${date}/document.pdf`;

  const params = new URLSearchParams();

  console.log(width, height);

  if (width) {
    params.append('width', `${width}`);
  }

  if (height) {
    params.append('height', `${height}`);
  }

  if (Array.from(params).length > 0) {
    URL = `${URL}?${params}`;
  }

  try {
    const response = await axiosInstance.get(URL, { responseType: 'blob' });
    return response.data;
  } catch (err) {
    throw new Error(err);
  }
}

export function useFoodGetCustomers(
  start_date: string | null,
  end_date: string | null,
  subscription_id: string | null,
  phone_number: string | null
) {
  let URL = endpoints.foods.customers;

  const params = new URLSearchParams();

  if (start_date && end_date) {
    params.append('start_date', `${start_date}`);
    params.append('end_date', `${end_date}`);
  }
  if (subscription_id) {
    params.append('subscription_id', subscription_id);
  }
  if (phone_number) {
    params.append('q', phone_number);
  }

  if (Array.from(params).length > 0) {
    URL = `${URL}?${params}`;
  }

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    const DATES = isLoading ? [] : data ? data?.data?.map((item: any) => item.date) : [];

    return {
      data: data as any,
      dates: DATES,
      dataLoading: isLoading,
      dataError: error,
      dataValidating: isValidating,
      dataEmpty: !isLoading && !data?.start_date && !data?.end_date,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}

export function useFoodGetIngredients(start_date: string | null, end_date: string | null) {
  let URL = endpoints.foods.ingredients;

  const params = new URLSearchParams();

  if (start_date && end_date) {
    params.append('start_date', `${start_date}`);
    params.append('end_date', `${end_date}`);
  }

  if (Array.from(params).length > 0) {
    URL = `${URL}?${params}`;
  }

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    const DATES = isLoading ? [] : data ? data?.data?.map((item: any) => item.date) : [];

    return {
      data: data as any,
      dates: DATES,
      dataLoading: isLoading,
      dataError: error,
      dataValidating: isValidating,
      dataEmpty: !isLoading && !data?.start_date && !data?.end_date,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}

export async function GetSelectedFoodExcel(date: string) {
  const URL = endpoints.foods.selected_food_excel(date);
  try {
    const response = await axiosInstance.get(URL, { responseType: 'blob' });
    return response.data;
  } catch (err) {
    throw new Error(err);
  }
}

export async function exportFoods(
  restaurant_id: string,
  mode: 'simple' | 'complex'
): Promise<{ blob: Blob; filename: string }> {
  if (!restaurant_id) {
    throw new Error('Restaurant ID is required');
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_HOST_API}/partners/foods/export/?restaurant=${restaurant_id}&mode=${mode}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Export failed' }));
      throw new Error(joinErrors(errorData));
    }

    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'food_export.xlsx'; // Default filename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    return { blob, filename };
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    }
    throw new Error('Failed to export foods');
  }
}

export async function importFoods(
  restaurant_id: string,
  mode: 'simple' | 'complex',
  file: File
): Promise<{ created: number; updated: number; errors: unknown[] }> {
  if (!restaurant_id) {
    throw new Error('Restaurant ID is required');
  }

  if (!file) {
    throw new Error('File is required');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);

  const { data, error } = await client.POST('/partners/foods/import/', {
    params: {
      query: {
        restaurant: Number(restaurant_id),
      } as any,
    },
    body: formData as any,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Failed to import food');
  }

  return data as { created: number; updated: number; errors: unknown[] };
}
